import { FormEvent, useEffect, useMemo, useRef, useState } from 'react'

type IconProps = { className?: string }

function PinIcon({ className }: IconProps) {
  return (
    <svg className={className} viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 21s6-5.18 6-11a6 6 0 1 0-12 0c0 5.82 6 11 6 11Z" />
      <circle cx="12" cy="10" r="2.2" />
    </svg>
  )
}

function ArrowIcon({ className }: IconProps) {
  return (
    <svg className={className} viewBox="0 0 24 24" aria-hidden="true">
      <path d="M5 12h13M14 7l5 5-5 5" />
    </svg>
  )
}

function GithubIcon({ className }: IconProps) {
  return (
    <svg className={className} viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 2a10 10 0 0 0-3.16 19.49c.5.09.68-.22.68-.48v-1.86c-2.78.6-3.37-1.18-3.37-1.18-.45-1.16-1.11-1.47-1.11-1.47-.91-.62.07-.61.07-.61 1 .07 1.53 1.03 1.53 1.03.9 1.53 2.35 1.09 2.92.83.09-.65.35-1.09.64-1.34-2.22-.25-4.56-1.11-4.56-4.94 0-1.09.39-1.98 1.03-2.68-.1-.25-.45-1.27.1-2.64 0 0 .84-.27 2.75 1.02A9.6 9.6 0 0 1 12 6.84a9.6 9.6 0 0 1 2.5.34c1.91-1.29 2.75-1.02 2.75-1.02.55 1.37.2 2.39.1 2.64.64.7 1.03 1.59 1.03 2.68 0 3.84-2.35 4.68-4.58 4.93.36.31.68.92.68 1.86V21c0 .27.18.58.69.48A10 10 0 0 0 12 2Z" />
    </svg>
  )
}

function LinkedInIcon({ className }: IconProps) {
  return (
    <svg className={className} viewBox="0 0 24 24" aria-hidden="true">
      <path d="M6.1 8.2H2.6V21h3.5V8.2ZM4.35 2A2.05 2.05 0 1 0 4.35 6.1 2.05 2.05 0 0 0 4.35 2ZM21.4 13.65c0-3.85-2.05-5.64-4.79-5.64-2.2 0-3.19 1.21-3.74 2.06V8.2H9.36V21h3.51v-6.34c0-1.67.32-3.29 2.39-3.29 2.04 0 2.07 1.91 2.07 3.4V21h3.51l.56-7.35Z" />
    </svg>
  )
}

function MenuIcon({ open }: { open: boolean }) {
  return (
    <span className={`menu-icon ${open ? 'menu-icon--open' : ''}`} aria-hidden="true">
      <span />
      <span />
    </span>
  )
}

export default function App() {
  const [menuOpen, setMenuOpen] = useState(false)
  const [notice, setNotice] = useState('')
  const [pointer, setPointer] = useState({ x: 0, y: 0 })
  const rootRef = useRef<HTMLElement>(null)
  const reduceMotion = useMemo(
    () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false,
    [],
  )

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMenuOpen(false)
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [])

  const handlePointerMove = (event: React.PointerEvent<HTMLElement>) => {
    if (reduceMotion) return
    const rect = event.currentTarget.getBoundingClientRect()
    const x = ((event.clientX - rect.left) / rect.width - 0.5) * 2
    const y = ((event.clientY - rect.top) / rect.height - 0.5) * 2
    setPointer({ x, y })
  }

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const form = new FormData(event.currentTarget)
    const email = String(form.get('email') ?? '').trim()

    if (!email) return
    setNotice('Launch notifications will open soon.')
    event.currentTarget.reset()
  }

  return (
    <main
      ref={rootRef}
      className="site-shell"
      onPointerMove={handlePointerMove}
      style={
        {
          '--mx': pointer.x.toFixed(3),
          '--my': pointer.y.toFixed(3),
        } as React.CSSProperties
      }
    >
      <div className="ambient ambient--one" />
      <div className="ambient ambient--two" />
      <div className="grain" aria-hidden="true" />
      <div className="edge-rule edge-rule--left" aria-hidden="true" />
      <div className="edge-rule edge-rule--right" aria-hidden="true" />

      <header className="topbar">
        <a className="brand" href="#top" aria-label="KEITH home">
          KEITH<span className="brand-dot" />
        </a>

        <nav className="desktop-nav" aria-label="Primary navigation">
          <a href="#about">About</a>
          <a href="#work">Work</a>
          <a href="#contact">Contact</a>
        </nav>

        <div className="topbar-meta">
          <span>©2026</span>
          <button
            className="menu-button"
            type="button"
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((value) => !value)}
          >
            <MenuIcon open={menuOpen} />
          </button>
        </div>
      </header>

      <div className={`menu-panel ${menuOpen ? 'menu-panel--open' : ''}`} aria-hidden={!menuOpen}>
        <div className="menu-panel-inner">
          <span className="eyebrow">NAVIGATION // 01</span>
          <a href="#about" onClick={() => setMenuOpen(false)}>About</a>
          <a href="#work" onClick={() => setMenuOpen(false)}>Work</a>
          <a href="#contact" onClick={() => setMenuOpen(false)}>Contact</a>
        </div>
      </div>

      <section id="top" className="hero" aria-label="Coming soon">
        <div className="status-card" aria-label="Current focus">
          <span className="status-label"><i /> BUILDING</span>
          <span className="status-copy">Code <b>•</b> AI <b>•</b> Cloud</span>
        </div>

        <div className="visual-wrap" aria-hidden="true">
          <div className="orbit orbit--one" />
          <div className="orbit orbit--two" />
          <img className="robot" src="/keith-robot.webp" alt="" />
          <span className="visual-index">K / 26</span>
        </div>

        <div className="headline-wrap">
          <p className="discipline">SOFTWARE ENGINEER <span>•</span> TECH BUILDER <span>•</span> SINGAPORE</p>
          <h1>
            <span>Coming</span>
            <span className="soon">Soon.</span>
          </h1>
        </div>

        <div id="about" className="intro-copy">
          <p>
            A new software engineering portfolio is launching in 2026. Built around thoughtful code,
            intelligent systems and digital experiences that create real impact.
          </p>
        </div>

        <form id="contact" className="notify-form" onSubmit={handleSubmit}>
          <div className="notify-copy">
            <span className="notify-title">Get notified</span>
            <span className="notify-subtitle">Be first to see the portfolio and latest work.</span>
          </div>
          <div className="notify-row">
            <label className="sr-only" htmlFor="email">Email address</label>
            <input
              id="email"
              name="email"
              type="email"
              inputMode="email"
              autoComplete="email"
              placeholder="Enter your email address"
              required
            />
            <button type="submit">
              Notify me <ArrowIcon className="arrow-icon" />
            </button>
          </div>
          <div className="form-notice" aria-live="polite">{notice}</div>
        </form>

        <div className="location-chip">
          <PinIcon className="pin-icon" />
          <span><b>Singapore</b><small>GMT+8</small></span>
        </div>

        <div className="socials" id="work">
          <span className="social-label">Follow the build</span>
          <div className="social-links">
            <a href="https://github.com/" target="_blank" rel="noreferrer" aria-label="GitHub">
              <GithubIcon />
            </a>
            <a href="https://www.linkedin.com/" target="_blank" rel="noreferrer" aria-label="LinkedIn">
              <LinkedInIcon />
            </a>
          </div>
        </div>

        <div className="footer-rhythm" aria-hidden="true">
          <span>BUILD</span><i />
          <span>LEARN</span><i />
          <span>CREATE</span><i />
          <span>SHIP</span>
        </div>
      </section>
    </main>
  )
}
